import type {
  MetaFunction,
  ActionFunctionArgs,
  LoaderFunctionArgs,
  ClientActionFunctionArgs,
} from "react-router";
import { useLoaderData, Link } from "react-router";
import { Form, useActionData, useNavigation } from "react-router";
import { useState, useCallback } from "react";
import { VoiceInput } from "~/components/voice-input";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { EmptyState } from "~/components/ui/empty-state";
import { ErrorBanner } from "~/components/ui/error-banner";
import { FormField } from "~/components/ui/form-field";
import { Label } from "~/components/ui/label";
import { PageHeader } from "~/components/ui/page-header";
import { Separator } from "~/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { useVoiceToText } from "~/hooks/use-voice-to-text";
import { historyService } from "~/services/history.server";
import { createRequestLogger } from "~/utils/logger";
import { appendLocalHistory, loadLocalHistory } from "~/lib/history-store";
import { isMockMode } from "~/mocks/mockEnv.server";
import { mockHistory } from "~/mocks/mockData";
import { getOptionalUser } from "~/services/auth.server";

export const meta: MetaFunction = () => {
  return [
    { title: "Fluentry - Mixed Language to Fluent English" },
    {
      name: "description",
      content:
        "Transform mixed-language input into natural English with multiple style variations.",
    },
  ];
};

type ConvertResult = {
  casual: string;
  formal: string;
  conversational: string;
  detectedLanguages?: string[];
  confidence?: number | null;
  processingTime?: number | null;
  explanation?: string;
};

type ConvertPayload = {
  success?: boolean;
  result?: ConvertResult;
  error?: string;
};

type ConvertApiResponse = {
  success: boolean;
  result?: ConvertResult;
  error?: {
    code: string;
    message: string;
  };
};

function getConvertError(payload: ConvertApiResponse): string {
  if (payload.error?.message && payload.error.message.length > 0) {
    return payload.error.message;
  }
  return "Network error";
}

async function postConvert(payload: {
  endpoint: string;
  body: FormData;
  request?: Request;
}): Promise<ConvertPayload | { error: string }> {
  const input = payload.body.get("text");
  const includeExplanation = payload.body.get("includeExplanation") === "on";
  if (!input || typeof input !== "string" || input.trim().length === 0) {
    return { error: "Please enter some text" };
  }

  let cookieHeader: string | undefined;
  if (payload.request) {
    const cookie = payload.request.headers.get("cookie");
    if (cookie) cookieHeader = cookie;
  }

  const res = await fetch(payload.endpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(cookieHeader ? { cookie: cookieHeader } : {}),
    },
    body: JSON.stringify({
      input: input.trim(),
      targetLanguage: "English",
      includeExplanation,
    }),
  });

  const data = (await res.json()) as ConvertApiResponse;
  if (!res.ok || !data.success || !data.result) {
    return { error: getConvertError(data) };
  }

  return {
    success: data.success,
    result: data.result,
  };
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const env = (context as any).cloudflare?.env || process.env || {};
  const logger = createRequestLogger({ scope: "routes._index.loader", env, request });
  if (isMockMode(env)) {
    return { history: mockHistory, user: null };
  }
  const user = await getOptionalUser(context, request);

  try {
    const historySvc = (context as any).historyService ?? historyService;
    const history = await historySvc.getRecentHistory(context, 5, user?.id);
    return { history, user };
  } catch (error) {
    logger.error("Failed to fetch history", { err: error });
    return { history: [], user };
  }
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const input = formData.get("text");
  const env = (context as any).cloudflare?.env || process.env || {};
  const logger = createRequestLogger({ scope: "routes._index.action", env, request });

  if (!input || typeof input !== "string" || input.trim().length === 0) {
    logger.warn("Invalid input", { inputType: typeof input });
    return { error: "Please enter some text" };
  }

  try {
    const payload = await postConvert({
      endpoint: new URL("/api/convert", request.url).toString(),
      body: formData,
      request,
    });

    if ("error" in payload) {
      return { error: payload.error };
    }
    return payload;
  } catch (error) {
    logger.error("Conversion error", { err: error });
    return { error: error instanceof Error ? error.message : "Conversion failed" };
  }
}

export async function clientLoader() {
  const logger = createRequestLogger({ scope: "routes._index.clientLoader" });
  try {
    const localHistory = loadLocalHistory();
    const res = await fetch("/api/history");
    if (!res.ok) throw new Error("API Error");
    const data = (await res.json()) as {
      success?: boolean;
      history?: Array<Record<string, any>>;
      error?: { code: string; message: string };
    };
    const remoteHistory = data.success ? (data.history ?? []) : [];
    const merged = [...remoteHistory, ...localHistory].reduce((acc: any[], item: any) => {
      if (!acc.find((existing) => existing.id === item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);
    return { history: merged, user: null };
  } catch (e) {
    logger.error("Client loader error", { err: e });
    return { history: loadLocalHistory(), user: null };
  }
}

export async function clientAction({ request }: ClientActionFunctionArgs) {
  const logger = createRequestLogger({ scope: "routes._index.clientAction", request });
  try {
    const body = await request.formData();
    const payload = await postConvert({
      endpoint: "/api/convert",
      body,
    });

    if ("error" in payload) {
      return { error: payload.error };
    }

    if (payload?.result) {
      const entry = {
        id: crypto.randomUUID(),
        input: body.get("text")?.toString() || "",
        targetLanguage: "en",
        casualOutput: payload.result.casual,
        formalOutput: payload.result.formal,
        conversationalOutput: payload.result.conversational,
        detectedLanguages: payload.result.detectedLanguages || [],
        confidence: payload.result.confidence || null,
        processingTime: payload.result.processingTime || null,
        createdAt: Date.now(),
      };
      appendLocalHistory(entry);
    }
    return payload;
  } catch (e) {
    logger.error("Client action error", { err: e });
    return { error: "Network request failed" };
  }
}

export default function ClassicPage() {
  const { history, user } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [inputText, setInputText] = useState("");
  const [includeExplanation, setIncludeExplanation] = useState(false);

  const { transcribeAudio, isProcessing } = useVoiceToText({
    onTranscription: (text) => {
      setInputText(text);
    },
    onError: (error) => {
      console.error("Voice recognition error:", error);
    },
  });

  const handleSpeechEnd = useCallback(
    async (audioData: Float32Array) => {
      try {
        await transcribeAudio(audioData);
      } catch (error) {
        console.error("Failed to transcribe audio:", error);
      }
    },
    [transcribeAudio],
  );

  const convertResult = actionData && "result" in actionData ? actionData.result : null;
  const convertError = actionData && "error" in actionData ? actionData.error : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Classic Converter"
        description="フォーム入力で混在言語テキストを英語に変換します。"
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/phonetic-resolver">聞き取り語推定</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/">新しいEditorへ</Link>
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
          {user ? (
            <div className="flex items-center gap-3">
              <Avatar>
                {user.image ? <AvatarImage src={user.image} alt={user.name || "User"} /> : null}
                <AvatarFallback>{user.email?.[0]?.toUpperCase() || "U"}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{user.name || user.email?.split("@")[0]}</p>
                <p className="text-xs text-muted-foreground">Signed in</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Guest mode</p>
          )}
          {user ? (
            <Form action="/api/auth/signout" method="post">
              <Button type="submit" variant="outline">
                Sign Out
              </Button>
            </Form>
          ) : (
            <Button asChild variant="outline">
              <Link to="/auth/signin">Sign In</Link>
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Convert Text</CardTitle>
            <CardDescription>入力を Casual / Formal / Conversational へ変換します。</CardDescription>
          </CardHeader>
          <CardContent>
            <Form method="post" className="space-y-5">
              <FormField label="Enter mixed-language text" htmlFor="text">
                <Textarea
                  id="text"
                  name="text"
                  rows={5}
                  placeholder="例: I want to 休む today because I'm とても tired..."
                  disabled={isSubmitting || isProcessing}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
              </FormField>

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="includeExplanation"
                    checked={includeExplanation}
                    onCheckedChange={(checked) => setIncludeExplanation(Boolean(checked))}
                  />
                  <Label htmlFor="includeExplanation" className="text-sm font-normal">
                    Include explanation (for learning)
                  </Label>
                </div>
                <VoiceInput onSpeechEnd={handleSpeechEnd} />
              </div>

              {includeExplanation ? <input type="hidden" name="includeExplanation" value="on" /> : null}

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || isProcessing || inputText.trim().length === 0}
              >
                {isSubmitting
                  ? "Converting..."
                  : isProcessing
                    ? "Processing voice..."
                    : "Convert to English"}
              </Button>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Examples</CardTitle>
            <CardDescription>入力イメージ</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <p className="font-medium text-muted-foreground">Input</p>
              <p>"I want to 休む today"</p>
            </div>
            <Separator />
            <div>
              <p className="font-medium text-muted-foreground">Input</p>
              <p>"This meeting is とても important"</p>
            </div>
            <Separator />
            <div>
              <p className="font-medium text-muted-foreground">Input</p>
              <p>"Can you help me with this 宿題?"</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conversion Result</CardTitle>
          <CardDescription>出力スタイルを切り替えて確認できます。</CardDescription>
        </CardHeader>
        <CardContent>
          {convertError ? <ErrorBanner message={convertError} /> : null}

          {convertResult ? (
            <div className="space-y-4">
              <Tabs defaultValue="casual">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="casual">Casual</TabsTrigger>
                  <TabsTrigger value="formal">Formal</TabsTrigger>
                  <TabsTrigger value="conversational">Conversational</TabsTrigger>
                </TabsList>
                <TabsContent value="casual" className="rounded-lg border p-4 text-sm">
                  {convertResult.casual}
                </TabsContent>
                <TabsContent value="formal" className="rounded-lg border p-4 text-sm">
                  {convertResult.formal}
                </TabsContent>
                <TabsContent value="conversational" className="rounded-lg border p-4 text-sm">
                  {convertResult.conversational}
                </TabsContent>
              </Tabs>

              {convertResult.explanation ? (
                <div className="rounded-lg border bg-muted/20 p-4 text-sm">
                  <p className="mb-1 font-medium">Explanation</p>
                  <p>{convertResult.explanation}</p>
                </div>
              ) : null}

              {convertResult.confidence ? (
                <p className="text-sm text-muted-foreground">
                  Confidence: {Math.round(convertResult.confidence * 100)}%
                </p>
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="まだ結果がありません"
              description="テキストを入力して Convert を実行してください。"
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent History</CardTitle>
          <CardDescription>最近の変換履歴</CardDescription>
        </CardHeader>
        <CardContent>
          {history && history.length > 0 ? (
            <div className="space-y-4">
              {history.map((item: any) => (
                <div key={item.id} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{new Date(item.createdAt).toLocaleString()}</p>
                  <p className="mt-1 text-sm italic">"{item.input}"</p>
                  <p className="mt-1 text-sm text-muted-foreground">➜ {item.casualOutput}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="履歴はまだありません" description="変換結果がここに保存されます。" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
